import { z } from 'zod';

import { PaymentCadenceEnum } from '../../../loan/EscrowSetting';
import { Checkbox, DateString, NonNegativeMonetaryValue } from '../../fields';

const TaxDetails = z.object({
  taxExempt: Checkbox.optional(),
  backupWithholdingRequired: Checkbox.optional(),
  backupWithholdingAmount: NonNegativeMonetaryValue.optional(),
});

export const TaxFields = z.object({
  taxDetails: TaxDetails.optional(),

  // COUNTY
  taxApn: z.string().optional().describe('Tax APN for the first property on the loan.'),
  taxCountyIsEscrowed: Checkbox.optional().describe(
    'County tax information for the first property on the loan.\n All fields are required when either field is present: taxCountyIsEscrowed OR taxCountyNextPaymentAmount > 0',
  ),
  taxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  taxCountyPayeeName: z.string().optional(),

  // CITY
  taxCityIsEscrowed: Checkbox.optional().describe(
    'City tax information for the first property on the loan. All fields are required when either field is present: taxCityIsEscrowed OR taxCityNextPaymentAmount > 0',
  ),
  taxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  taxCityPayeeName: z.string().optional(),
  taxCityNextDueDate: DateString.optional(),
  taxCityPaymentCadence: PaymentCadenceEnum.optional(),

  // SCHOOL
  taxSchoolIsEscrowed: Checkbox.optional().describe(
    'School tax information for the first property on the loan. All fields are required when either field is present: taxSchoolIsEscrowed OR taxSchoolNextPaymentAmount > 0',
  ),
  taxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  taxSchoolPayeeName: z.string().optional(),
  taxSchoolNextDueDate: DateString.optional(),
  taxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  // Other tax 1
  taxOther1IsEscrowed: Checkbox.optional().describe(
    'Other tax information for the first property on the loan. All fields are required when either field is present: taxOther1IsEscrowed OR taxOther1NextPaymentAmount > 0',
  ),
  taxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  taxOther1PayeeName: z.string().optional(),
  taxOther1NextDueDate: DateString.optional(),
  taxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  // Other tax 2
  taxOther2IsEscrowed: Checkbox.optional(),
  taxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  taxOther2PayeeName: z.string().optional(),
  taxOther2NextDueDate: DateString.optional(),
  taxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  // Other tax 3
  taxOther3IsEscrowed: Checkbox.optional(),
  taxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  taxOther3PayeeName: z.string().optional(),
  taxOther3NextDueDate: DateString.optional(),
  taxOther3PaymentCadence: PaymentCadenceEnum.optional(),
});

export const MultipleAddressesTaxFields = z.object({
  // Tax county
  address2TaxApn: z.string().optional(),
  address2TaxCountyIsEscrowed: Checkbox.optional(),
  address2TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address2TaxCountyPayeeName: z.string().optional(),

  address3TaxApn: z.string().optional(),
  address3TaxCountyIsEscrowed: Checkbox.optional(),
  address3TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address3TaxCountyPayeeName: z.string().optional(),

  address4TaxApn: z.string().optional(),
  address4TaxCountyIsEscrowed: Checkbox.optional(),
  address4TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address4TaxCountyPayeeName: z.string().optional(),

  address5TaxApn: z.string().optional(),
  address5TaxCountyIsEscrowed: Checkbox.optional(),
  address5TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address5TaxCountyPayeeName: z.string().optional(),

  address6TaxApn: z.string().optional(),
  address6TaxCountyIsEscrowed: Checkbox.optional(),
  address6TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address6TaxCountyPayeeName: z.string().optional(),

  address7TaxApn: z.string().optional(),
  address7TaxCountyIsEscrowed: Checkbox.optional(),
  address7TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address7TaxCountyPayeeName: z.string().optional(),

  address8TaxApn: z.string().optional(),
  address8TaxCountyIsEscrowed: Checkbox.optional(),
  address8TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address8TaxCountyPayeeName: z.string().optional(),

  address9TaxApn: z.string().optional(),
  address9TaxCountyIsEscrowed: Checkbox.optional(),
  address9TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address9TaxCountyPayeeName: z.string().optional(),

  address10TaxApn: z.string().optional(),
  address10TaxCountyIsEscrowed: Checkbox.optional(),
  address10TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address10TaxCountyPayeeName: z.string().optional(),

  address11TaxApn: z.string().optional(),
  address11TaxCountyIsEscrowed: Checkbox.optional(),
  address11TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address11TaxCountyPayeeName: z.string().optional(),

  address12TaxApn: z.string().optional(),
  address12TaxCountyIsEscrowed: Checkbox.optional(),
  address12TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address12TaxCountyPayeeName: z.string().optional(),

  address13TaxApn: z.string().optional(),
  address13TaxCountyIsEscrowed: Checkbox.optional(),
  address13TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address13TaxCountyPayeeName: z.string().optional(),

  address14TaxApn: z.string().optional(),
  address14TaxCountyIsEscrowed: Checkbox.optional(),
  address14TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address14TaxCountyPayeeName: z.string().optional(),

  address15TaxApn: z.string().optional(),
  address15TaxCountyIsEscrowed: Checkbox.optional(),
  address15TaxCountyNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address15TaxCountyPayeeName: z.string().optional(),

  // Tax city
  address2TaxCityIsEscrowed: Checkbox.optional(),
  address2TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address2TaxCityPayeeName: z.string().optional(),
  address2TaxCityNextDueDate: DateString.optional(),
  address2TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address3TaxCityIsEscrowed: Checkbox.optional(),
  address3TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address3TaxCityPayeeName: z.string().optional(),
  address3TaxCityNextDueDate: DateString.optional(),
  address3TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address4TaxCityIsEscrowed: Checkbox.optional(),
  address4TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address4TaxCityPayeeName: z.string().optional(),
  address4TaxCityNextDueDate: DateString.optional(),
  address4TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address5TaxCityIsEscrowed: Checkbox.optional(),
  address5TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address5TaxCityPayeeName: z.string().optional(),
  address5TaxCityNextDueDate: DateString.optional(),
  address5TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address6TaxCityIsEscrowed: Checkbox.optional(),
  address6TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address6TaxCityPayeeName: z.string().optional(),
  address6TaxCityNextDueDate: DateString.optional(),
  address6TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address7TaxCityIsEscrowed: Checkbox.optional(),
  address7TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address7TaxCityPayeeName: z.string().optional(),
  address7TaxCityNextDueDate: DateString.optional(),
  address7TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address8TaxCityIsEscrowed: Checkbox.optional(),
  address8TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address8TaxCityPayeeName: z.string().optional(),
  address8TaxCityNextDueDate: DateString.optional(),
  address8TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address9TaxCityIsEscrowed: Checkbox.optional(),
  address9TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address9TaxCityPayeeName: z.string().optional(),
  address9TaxCityNextDueDate: DateString.optional(),
  address9TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address10TaxCityIsEscrowed: Checkbox.optional(),
  address10TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address10TaxCityPayeeName: z.string().optional(),
  address10TaxCityNextDueDate: DateString.optional(),
  address10TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address11TaxCityIsEscrowed: Checkbox.optional(),
  address11TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address11TaxCityPayeeName: z.string().optional(),
  address11TaxCityNextDueDate: DateString.optional(),
  address11TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address12TaxCityIsEscrowed: Checkbox.optional(),
  address12TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address12TaxCityPayeeName: z.string().optional(),
  address12TaxCityNextDueDate: DateString.optional(),
  address12TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address13TaxCityIsEscrowed: Checkbox.optional(),
  address13TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address13TaxCityPayeeName: z.string().optional(),
  address13TaxCityNextDueDate: DateString.optional(),
  address13TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address14TaxCityIsEscrowed: Checkbox.optional(),
  address14TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address14TaxCityPayeeName: z.string().optional(),
  address14TaxCityNextDueDate: DateString.optional(),
  address14TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  address15TaxCityIsEscrowed: Checkbox.optional(),
  address15TaxCityNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address15TaxCityPayeeName: z.string().optional(),
  address15TaxCityNextDueDate: DateString.optional(),
  address15TaxCityPaymentCadence: PaymentCadenceEnum.optional(),

  // Tax school
  address2TaxSchoolIsEscrowed: Checkbox.optional(),
  address2TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address2TaxSchoolPayeeName: z.string().optional(),
  address2TaxSchoolNextDueDate: DateString.optional(),
  address2TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address3TaxSchoolIsEscrowed: Checkbox.optional(),
  address3TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address3TaxSchoolPayeeName: z.string().optional(),
  address3TaxSchoolNextDueDate: DateString.optional(),
  address3TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address4TaxSchoolIsEscrowed: Checkbox.optional(),
  address4TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address4TaxSchoolPayeeName: z.string().optional(),
  address4TaxSchoolNextDueDate: DateString.optional(),
  address4TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address5TaxSchoolIsEscrowed: Checkbox.optional(),
  address5TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address5TaxSchoolPayeeName: z.string().optional(),
  address5TaxSchoolNextDueDate: DateString.optional(),
  address5TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address6TaxSchoolIsEscrowed: Checkbox.optional(),
  address6TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address6TaxSchoolPayeeName: z.string().optional(),
  address6TaxSchoolNextDueDate: DateString.optional(),
  address6TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address7TaxSchoolIsEscrowed: Checkbox.optional(),
  address7TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address7TaxSchoolPayeeName: z.string().optional(),
  address7TaxSchoolNextDueDate: DateString.optional(),
  address7TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address8TaxSchoolIsEscrowed: Checkbox.optional(),
  address8TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address8TaxSchoolPayeeName: z.string().optional(),
  address8TaxSchoolNextDueDate: DateString.optional(),
  address8TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address9TaxSchoolIsEscrowed: Checkbox.optional(),
  address9TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address9TaxSchoolPayeeName: z.string().optional(),
  address9TaxSchoolNextDueDate: DateString.optional(),
  address9TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address10TaxSchoolIsEscrowed: Checkbox.optional(),
  address10TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address10TaxSchoolPayeeName: z.string().optional(),
  address10TaxSchoolNextDueDate: DateString.optional(),
  address10TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address11TaxSchoolIsEscrowed: Checkbox.optional(),
  address11TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address11TaxSchoolPayeeName: z.string().optional(),
  address11TaxSchoolNextDueDate: DateString.optional(),
  address11TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address12TaxSchoolIsEscrowed: Checkbox.optional(),

  address12TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address12TaxSchoolPayeeName: z.string().optional(),
  address12TaxSchoolNextDueDate: DateString.optional(),
  address12TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address13TaxSchoolIsEscrowed: Checkbox.optional(),
  address13TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address13TaxSchoolPayeeName: z.string().optional(),
  address13TaxSchoolNextDueDate: DateString.optional(),
  address13TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address14TaxSchoolIsEscrowed: Checkbox.optional(),
  address14TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address14TaxSchoolPayeeName: z.string().optional(),
  address14TaxSchoolNextDueDate: DateString.optional(),
  address14TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  address15TaxSchoolIsEscrowed: Checkbox.optional(),
  address15TaxSchoolNextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address15TaxSchoolPayeeName: z.string().optional(),
  address15TaxSchoolNextDueDate: DateString.optional(),
  address15TaxSchoolPaymentCadence: PaymentCadenceEnum.optional(),

  // Other tax 1
  address2TaxOther1IsEscrowed: Checkbox.optional(),
  address2TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address2TaxOther1PayeeName: z.string().optional(),
  address2TaxOther1NextDueDate: DateString.optional(),
  address2TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address3TaxOther1IsEscrowed: Checkbox.optional(),
  address3TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address3TaxOther1PayeeName: z.string().optional(),
  address3TaxOther1NextDueDate: DateString.optional(),
  address3TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address4TaxOther1IsEscrowed: Checkbox.optional(),
  address4TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address4TaxOther1PayeeName: z.string().optional(),
  address4TaxOther1NextDueDate: DateString.optional(),
  address4TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address5TaxOther1IsEscrowed: Checkbox.optional(),
  address5TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address5TaxOther1PayeeName: z.string().optional(),
  address5TaxOther1NextDueDate: DateString.optional(),
  address5TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address6TaxOther1IsEscrowed: Checkbox.optional(),
  address6TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address6TaxOther1PayeeName: z.string().optional(),
  address6TaxOther1NextDueDate: DateString.optional(),
  address6TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address7TaxOther1IsEscrowed: Checkbox.optional(),
  address7TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address7TaxOther1PayeeName: z.string().optional(),
  address7TaxOther1NextDueDate: DateString.optional(),
  address7TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address8TaxOther1IsEscrowed: Checkbox.optional(),
  address8TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address8TaxOther1PayeeName: z.string().optional(),
  address8TaxOther1NextDueDate: DateString.optional(),
  address8TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address9TaxOther1IsEscrowed: Checkbox.optional(),
  address9TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address9TaxOther1PayeeName: z.string().optional(),
  address9TaxOther1NextDueDate: DateString.optional(),
  address9TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address10TaxOther1IsEscrowed: Checkbox.optional(),
  address10TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address10TaxOther1PayeeName: z.string().optional(),
  address10TaxOther1NextDueDate: DateString.optional(),
  address10TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address11TaxOther1IsEscrowed: Checkbox.optional(),
  address11TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address11TaxOther1PayeeName: z.string().optional(),
  address11TaxOther1NextDueDate: DateString.optional(),
  address11TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address12TaxOther1IsEscrowed: Checkbox.optional(),
  address12TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address12TaxOther1PayeeName: z.string().optional(),
  address12TaxOther1NextDueDate: DateString.optional(),
  address12TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address13TaxOther1IsEscrowed: Checkbox.optional(),
  address13TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address13TaxOther1PayeeName: z.string().optional(),
  address13TaxOther1NextDueDate: DateString.optional(),
  address13TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address14TaxOther1IsEscrowed: Checkbox.optional(),
  address14TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address14TaxOther1PayeeName: z.string().optional(),
  address14TaxOther1NextDueDate: DateString.optional(),
  address14TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  address15TaxOther1IsEscrowed: Checkbox.optional(),
  address15TaxOther1NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address15TaxOther1PayeeName: z.string().optional(),
  address15TaxOther1NextDueDate: DateString.optional(),
  address15TaxOther1PaymentCadence: PaymentCadenceEnum.optional(),

  // Other tax 2
  address2TaxOther2IsEscrowed: Checkbox.optional(),
  address2TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address2TaxOther2PayeeName: z.string().optional(),
  address2TaxOther2NextDueDate: DateString.optional(),
  address2TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address3TaxOther2IsEscrowed: Checkbox.optional(),
  address3TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address3TaxOther2PayeeName: z.string().optional(),
  address3TaxOther2NextDueDate: DateString.optional(),
  address3TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address4TaxOther2IsEscrowed: Checkbox.optional(),
  address4TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address4TaxOther2PayeeName: z.string().optional(),
  address4TaxOther2NextDueDate: DateString.optional(),
  address4TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address5TaxOther2IsEscrowed: Checkbox.optional(),
  address5TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address5TaxOther2PayeeName: z.string().optional(),
  address5TaxOther2NextDueDate: DateString.optional(),
  address5TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address6TaxOther2IsEscrowed: Checkbox.optional(),
  address6TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address6TaxOther2PayeeName: z.string().optional(),
  address6TaxOther2NextDueDate: DateString.optional(),
  address6TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address7TaxOther2IsEscrowed: Checkbox.optional(),
  address7TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address7TaxOther2PayeeName: z.string().optional(),
  address7TaxOther2NextDueDate: DateString.optional(),
  address7TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address8TaxOther2IsEscrowed: Checkbox.optional(),
  address8TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address8TaxOther2PayeeName: z.string().optional(),
  address8TaxOther2NextDueDate: DateString.optional(),
  address8TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address9TaxOther2IsEscrowed: Checkbox.optional(),
  address9TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address9TaxOther2PayeeName: z.string().optional(),
  address9TaxOther2NextDueDate: DateString.optional(),
  address9TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address10TaxOther2IsEscrowed: Checkbox.optional(),
  address10TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address10TaxOther2PayeeName: z.string().optional(),
  address10TaxOther2NextDueDate: DateString.optional(),
  address10TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address11TaxOther2IsEscrowed: Checkbox.optional(),
  address11TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address11TaxOther2PayeeName: z.string().optional(),
  address11TaxOther2NextDueDate: DateString.optional(),
  address11TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address12TaxOther2IsEscrowed: Checkbox.optional(),
  address12TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address12TaxOther2PayeeName: z.string().optional(),
  address12TaxOther2NextDueDate: DateString.optional(),
  address12TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address13TaxOther2IsEscrowed: Checkbox.optional(),
  address13TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address13TaxOther2PayeeName: z.string().optional(),
  address13TaxOther2NextDueDate: DateString.optional(),
  address13TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address14TaxOther2IsEscrowed: Checkbox.optional(),
  address14TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address14TaxOther2PayeeName: z.string().optional(),
  address14TaxOther2NextDueDate: DateString.optional(),
  address14TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  address15TaxOther2IsEscrowed: Checkbox.optional(),
  address15TaxOther2NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address15TaxOther2PayeeName: z.string().optional(),
  address15TaxOther2NextDueDate: DateString.optional(),
  address15TaxOther2PaymentCadence: PaymentCadenceEnum.optional(),

  // Other tax 3
  address2TaxOther3IsEscrowed: Checkbox.optional(),
  address2TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address2TaxOther3PayeeName: z.string().optional(),
  address2TaxOther3NextDueDate: DateString.optional(),
  address2TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address3TaxOther3IsEscrowed: Checkbox.optional(),
  address3TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address3TaxOther3PayeeName: z.string().optional(),
  address3TaxOther3NextDueDate: DateString.optional(),
  address3TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address4TaxOther3IsEscrowed: Checkbox.optional(),
  address4TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address4TaxOther3PayeeName: z.string().optional(),
  address4TaxOther3NextDueDate: DateString.optional(),
  address4TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address5TaxOther3IsEscrowed: Checkbox.optional(),
  address5TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address5TaxOther3PayeeName: z.string().optional(),
  address5TaxOther3NextDueDate: DateString.optional(),
  address5TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address6TaxOther3IsEscrowed: Checkbox.optional(),
  address6TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address6TaxOther3PayeeName: z.string().optional(),
  address6TaxOther3NextDueDate: DateString.optional(),
  address6TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address7TaxOther3IsEscrowed: Checkbox.optional(),
  address7TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address7TaxOther3PayeeName: z.string().optional(),
  address7TaxOther3NextDueDate: DateString.optional(),
  address7TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address8TaxOther3IsEscrowed: Checkbox.optional(),
  address8TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address8TaxOther3PayeeName: z.string().optional(),
  address8TaxOther3NextDueDate: DateString.optional(),
  address8TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address9TaxOther3IsEscrowed: Checkbox.optional(),
  address9TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address9TaxOther3PayeeName: z.string().optional(),
  address9TaxOther3NextDueDate: DateString.optional(),
  address9TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address10TaxOther3IsEscrowed: Checkbox.optional(),
  address10TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address10TaxOther3PayeeName: z.string().optional(),
  address10TaxOther3NextDueDate: DateString.optional(),
  address10TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address11TaxOther3IsEscrowed: Checkbox.optional(),
  address11TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address11TaxOther3PayeeName: z.string().optional(),
  address11TaxOther3NextDueDate: DateString.optional(),
  address11TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address12TaxOther3IsEscrowed: Checkbox.optional(),
  address12TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address12TaxOther3PayeeName: z.string().optional(),
  address12TaxOther3NextDueDate: DateString.optional(),
  address12TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address13TaxOther3IsEscrowed: Checkbox.optional(),
  address13TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address13TaxOther3PayeeName: z.string().optional(),
  address13TaxOther3NextDueDate: DateString.optional(),
  address13TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address14TaxOther3IsEscrowed: Checkbox.optional(),
  address14TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address14TaxOther3PayeeName: z.string().optional(),
  address14TaxOther3NextDueDate: DateString.optional(),
  address14TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),

  address15TaxOther3IsEscrowed: Checkbox.optional(),
  address15TaxOther3NextPaymentAmount: NonNegativeMonetaryValue.optional(),
  address15TaxOther3PayeeName: z.string().optional(),
  address15TaxOther3NextDueDate: DateString.optional(),
  address15TaxOther3PaymentCadence: PaymentCadenceEnum.optional(),
});

export type TaxFields = z.infer<typeof TaxFields>;
export type MultipleAddressesTaxFields = z.infer<typeof MultipleAddressesTaxFields>;

export type SingleTaxCountyFields = Pick<
  TaxFields,
  'taxApn' | 'taxCountyIsEscrowed' | 'taxCountyNextPaymentAmount' | 'taxCountyPayeeName'
>;

export type SingleTaxCityFields = Pick<
  TaxFields,
  'taxCityIsEscrowed' | 'taxCityNextPaymentAmount' | 'taxCityNextDueDate' | 'taxCityPayeeName' | 'taxCityPaymentCadence'
>;

export type SingleTaxSchoolFields = Pick<
  TaxFields,
  | 'taxSchoolIsEscrowed'
  | 'taxSchoolNextPaymentAmount'
  | 'taxSchoolNextDueDate'
  | 'taxSchoolPayeeName'
  | 'taxSchoolPaymentCadence'
>;

export type SingleTaxOtherFields = Pick<
  TaxFields,
  | 'taxOther1IsEscrowed'
  | 'taxOther1NextPaymentAmount'
  | 'taxOther1PayeeName'
  | 'taxOther1NextDueDate'
  | 'taxOther1PaymentCadence'
>;
